/**
 * useGroupTree — lazy, server-driven 1C-style group tree for RuntimeListView.
 *
 * Groups load one level at a time from the `group_by` list endpoint; each
 * expanded LEAF group windows its own rows through a dedicated
 * `WindowedRowCache`. A single flattened `entries` array feeds one virtualizer
 * in `RuntimeGroupTreeView`.
 *
 * Ownership:
 *   - a `GroupTreeModel` (pure expansion/summary state) in a ref — recreated
 *     whenever the query changes (connection/object/levels/filters/search/
 *     showDeleted/sort), honouring the model's one-instance-per-query contract;
 *   - a `Map<groupKey, WindowedRowCache>` of caches for expanded leaf groups,
 *     each disposed on collapse / reset / unmount;
 *   - a `Map<groupKey, values>` of the raw (ref-display-enriched) group value
 *     objects so headers can render ref/date/number labels like data cells.
 *
 * A version counter forces re-renders; `entries` is recomputed from the model
 * on every render (cheap depth-first flatten of only the visible nodes).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { runtimeApi } from '@/app/services/runtimeApi'
import {
  GroupTreeModel,
  type FlatEntry,
  type GroupPath,
  type GroupSummary,
} from '@/shared/listViews/groupTreeModel'
import { WindowedRowCache, type RowState } from './windowedRowCache'

/** Server page size + JS row chunk size for per-group windowed rows. */
const CHUNK_SIZE = 50
/** LRU cap on chunks held in memory, per expanded leaf group. */
const MAX_PAGES_IN_MEMORY = 20

export interface UseGroupTreeOptions {
  connectionId: string
  objectName: string
  /** Disables all fetching while false (config not loaded / not in grouped mode). */
  enabled: boolean
  /** Ordered grouping fields (level 0 = outermost). */
  levels: string[]
  /** Base equality filters (schema tabs/filter_fields), merged with parent-path values. */
  baseFilters: Record<string, unknown>
  search: string
  searchFields?: string[]
  showDeleted: boolean
  sortField: string
  sortDir: 'asc' | 'desc'
  /** Active i18next locale — resolves translatable reference display server-side. */
  locale?: string
}

export interface UseGroupTreeResult {
  /** Depth-first flattened visible entries (group headers + windowed rows). */
  entries: FlatEntry[]
  /** Total record count (sum of root-level group counts). */
  total: number
  /** Expand/collapse a group; drives child-level fetches or leaf row windows. */
  toggle: (path: GroupPath) => void
  /** Resolve a windowed row entry to its cached row/loading/placeholder state. */
  rowAt: (entry: Extract<FlatEntry, { kind: 'row' }>) => RowState
  /** Raw (ref-display-enriched) value object for a group path, for header labels. */
  groupValues: (path: GroupPath) => Record<string, unknown> | undefined
  /** True while the root level is loading. */
  loading: boolean
  error: string | null
  /** Rebuild the tree from scratch (root reload + drop all caches/expansion). */
  reload: () => void
}

export function useGroupTree(opts: UseGroupTreeOptions): UseGroupTreeResult {
  const {
    connectionId,
    objectName,
    enabled,
    levels,
    baseFilters,
    search,
    searchFields,
    showDeleted,
    sortField,
    sortDir,
    locale,
  } = opts

  const [, setVersion] = useState(0)
  const bump = useCallback(() => setVersion((v) => v + 1), [])

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const modelRef = useRef<GroupTreeModel | null>(null)
  const cachesRef = useRef<Map<string, WindowedRowCache>>(new Map())
  const valuesByKeyRef = useRef<Map<string, Record<string, unknown>>>(new Map())
  const rootTotalRef = useRef(0)

  // Lazily seed a model on first render so `entries` is never read from null
  // before the reset effect runs. The effect owns every subsequent recreation.
  if (modelRef.current === null) modelRef.current = new GroupTreeModel(levels)

  // Latest option values for callbacks (toggle/loadLevel/ensureCache) that run
  // long after render — reading a ref avoids stale closures and dep churn.
  const stateRef = useRef(opts)
  stateRef.current = opts

  const pathToFilters = useCallback(
    (path: GroupPath): Record<string, unknown> =>
      Object.fromEntries(path.map((p) => [p.field, p.value])),
    [],
  )

  const storeValues = useCallback((path: GroupPath, groups: GroupSummary[]) => {
    const model = modelRef.current
    if (!model) return
    const field = stateRef.current.levels[path.length]
    if (!field) return
    for (const g of groups) {
      const childKey = model.keyOf([...path, { field, value: g.values[field] }])
      valuesByKeyRef.current.set(childKey, g.values)
    }
  }, [])

  const disposeAllCaches = useCallback(() => {
    cachesRef.current.forEach((c) => c.dispose())
    cachesRef.current.clear()
  }, [])

  const disposeCache = useCallback((path: GroupPath) => {
    const model = modelRef.current
    if (!model) return
    const key = model.keyOf(path)
    const cache = cachesRef.current.get(key)
    if (cache) {
      cache.dispose()
      cachesRef.current.delete(key)
    }
  }, [])

  const ensureCache = useCallback((path: GroupPath) => {
    const s = stateRef.current
    const model = modelRef.current
    if (!model) return
    const key = model.keyOf(path)
    if (cachesRef.current.has(key)) return
    const filters = { ...s.baseFilters, ...pathToFilters(path) }
    const cache = new WindowedRowCache({
      chunkSize: CHUNK_SIZE,
      maxPagesInMemory: MAX_PAGES_IN_MEMORY,
      onChange: bump,
      fetchPage: (page, size) =>
        runtimeApi
          .list(s.connectionId, s.objectName, {
            page,
            limit: size,
            search: s.search,
            sort_field: s.sortField,
            sort_dir: s.sortDir,
            include_deleted: s.showDeleted,
            filters,
            search_fields: s.searchFields,
            locale: s.locale,
          })
          .then((r) => ({ rows: r.data ?? [], total: r.meta?.total ?? 0 })),
    })
    cachesRef.current.set(key, cache)
  }, [bump, pathToFilters])

  // Fetch one group level. `path` = [] loads the root (level 0); otherwise it
  // loads level `path.length` filtered by the parent path's equality values.
  const loadLevel = useCallback(
    async (path: GroupPath) => {
      const s = stateRef.current
      const model = modelRef.current
      if (!model) return
      const field = s.levels[path.length]
      if (!field) return
      try {
        const resp = await runtimeApi.listGroups(s.connectionId, s.objectName, {
          group_by: [field],
          filters: { ...s.baseFilters, ...pathToFilters(path) },
          search: s.search,
          search_fields: s.searchFields,
          include_deleted: s.showDeleted,
          locale: s.locale,
        })
        // Discard results from a superseded query (model was recreated on reset).
        if (modelRef.current !== model) return
        const groups = resp.groups ?? []
        model.setGroups(path, groups)
        storeValues(path, groups)
        if (path.length === 0) {
          rootTotalRef.current = groups.reduce((sum, g) => sum + (g.count || 0), 0)
        }
        setError(null)
      } catch (e) {
        if (modelRef.current !== model) return
        setError(e instanceof Error ? e.message : 'Failed to load groups')
      } finally {
        if (modelRef.current === model) bump()
      }
    },
    [bump, pathToFilters, storeValues],
  )

  const toggle = useCallback(
    (path: GroupPath) => {
      const s = stateRef.current
      const model = modelRef.current
      if (!model) return
      model.toggle(path)
      const expanded = model.isExpanded(path)
      const isLeafLevel = path.length >= s.levels.length
      if (expanded) {
        if (!isLeafLevel) {
          if (model.needsChildren(path)) void loadLevel(path)
        } else {
          ensureCache(path)
        }
      } else if (isLeafLevel) {
        disposeCache(path)
      }
      bump()
    },
    [bump, ensureCache, disposeCache, loadLevel],
  )

  const rowAt = useCallback((entry: Extract<FlatEntry, { kind: 'row' }>): RowState => {
    const model = modelRef.current
    if (!model) return { state: 'loading' }
    const cache = cachesRef.current.get(model.keyOf(entry.groupPath))
    if (!cache) return { state: 'loading' }
    return cache.getRow(entry.rowIndex)
  }, [])

  const groupValues = useCallback((path: GroupPath): Record<string, unknown> | undefined => {
    const model = modelRef.current
    if (!model) return undefined
    return valuesByKeyRef.current.get(model.keyOf(path))
  }, [])

  const [reloadNonce, setReloadNonce] = useState(0)
  const reload = useCallback(() => setReloadNonce((n) => n + 1), [])

  const levelsKey = levels.join('')
  const baseFiltersKey = JSON.stringify(baseFilters ?? {})
  const searchFieldsKey = (searchFields ?? []).join(',')

  // Reset + root load on any query change. Recreating the model (per its
  // one-instance-per-query contract) drops stale expansion/children; caches are
  // disposed here and on unmount.
  useEffect(() => {
    disposeAllCaches()
    valuesByKeyRef.current = new Map()
    modelRef.current = new GroupTreeModel(levels)
    rootTotalRef.current = 0
    setError(null)

    if (!enabled || levels.length === 0) {
      setLoading(false)
      bump()
      return () => disposeAllCaches()
    }

    setLoading(true)
    bump()
    let cancelled = false
    void loadLevel([]).finally(() => {
      if (!cancelled) setLoading(false)
    })
    return () => {
      cancelled = true
      disposeAllCaches()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    connectionId,
    objectName,
    levelsKey,
    baseFiltersKey,
    search,
    searchFieldsKey,
    showDeleted,
    sortField,
    sortDir,
    locale,
    reloadNonce,
  ])

  const entries = modelRef.current ? modelRef.current.flatten() : []

  return {
    entries,
    total: rootTotalRef.current,
    toggle,
    rowAt,
    groupValues,
    loading,
    error,
    reload,
  }
}
